import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import type { Org, Agent as RbacAgent } from '@/types';

interface HeaderDataContextValue {
  orgs: Org[];
  agents: RbacAgent[];
  /** Re-fetch orgs + agents from server. Call after creating/deleting/renaming. */
  refresh: () => Promise<void>;
}

const HeaderDataContext = createContext<HeaderDataContextValue>({
  orgs: [],
  agents: [],
  refresh: async () => {},
});

export function HeaderDataProvider({ children }: { children: ReactNode }) {
  const { user, hasRole } = useAuth();
  const isSuperAdmin = hasRole('super_admin');

  const [orgs, setOrgs] = useState<Org[]>([]);
  const [agents, setAgents] = useState<RbacAgent[]>([]);

  // Use refs to avoid refresh identity changing on every render
  const userRef = useRef(user);
  const isSuperAdminRef = useRef(isSuperAdmin);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { isSuperAdminRef.current = isSuperAdmin; }, [isSuperAdmin]);

  // Stable refresh — identity never changes, safe to use in useEffect deps
  const refresh = useCallback(async () => {
    if (!userRef.current) return;
    try {
      const [a, o] = await Promise.all([
        api.getUserVisibleAgents(),
        isSuperAdminRef.current ? api.getUserVisibleOrgs() : Promise.resolve([]),
      ]);
      setAgents(a || []);
      setOrgs(o || []);
    } catch (err) {
      console.error('Failed to refresh header data:', err);
    }
  }, []); // stable — no deps

  // Load once when the user becomes available (login / page restore)
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = user?.id ?? null;
    if (uid && uid !== userIdRef.current) {
      userIdRef.current = uid;
      refresh();
    } else if (!uid) {
      userIdRef.current = null;
    }
  }, [user, refresh]);

  return (
    <HeaderDataContext.Provider value={{ orgs, agents, refresh }}>
      {children}
    </HeaderDataContext.Provider>
  );
}

export function useHeaderData() {
  return useContext(HeaderDataContext);
}
